/**
 * Returns a function, that, as long as it continues to be invoked, will not
 * be triggered. The func callback is ensured to be called after it stops being called for
 * debounceTime milliseconds. If `immediate` is passed, trigger the function on the
 * leading edge, instead of the trailing.
 * @param {Function} func
 * @param {Number} debounceTime
 * @param {Boolean} immediate
 * @returns {Function}
 */
export function debounce(func, debounceTime, immediate) {
    var timeout;

    return function () {
        var context = this, args = arguments;
        var later = function () {
            timeout = null;
            if (!immediate) func.apply(context, args);
        };
        var callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, debounceTime);
        if (callNow) func.apply(context, args);
    };
};

/**
 * Returns a function that will reduce the calls to the func callback. Upon this returned
 * function call, it will only call the func callback if intervalTime milliseconds have passed
 * since the last function call. If not, it will put it off back to be called later after
 * intervalTime milliseconds.
 * @param {Function} func
 * @param {Number} intervalTime
 * @returns {Function}
 */
export function throttle(func, intervalTime = 20) {
    var wait = false;

    return function () {
        var context = this, args = arguments
        if (!wait) {
            func.apply(context, args);

            wait = true;
            setTimeout(function () {
                wait = false;
            }, intervalTime);
        }
    }
}

/**
 * Creates an accurate timer abstraction that will compare to Date.now()
 * Credit goes to https://stackoverflow.com/a/57981688/13931027
 */
export class Timer {
    constructor() {
        this.isRunning = false;
        this.startTime = 0;
        this.overallTime = 0;
    }

    getTimeElapsedSinceLastStart() {
        if (!this.startTime) {
            return 0;
        }

        return Date.now() - this.startTime;
    }

    start() {
        if (this.isRunning) {
            return console.error('Timer is already running');
        }

        this.isRunning = true;
        this.startTime = Date.now();
    }

    stop() {
        if (!this.isRunning) {
            return console.error('Timer is already stopped');
        }

        this.isRunning = false;
        this.overallTime = this.overallTime + this.getTimeElapsedSinceLastStart();
    }

    reset() {
        this.overallTime = 0;

        if (this.isRunning) {
            this.startTime = Date.now();
            return;
        }

        this.startTime = 0;
    }

    getTime() {
        if (!this.startTime) {
            return 0;
        }

        if (this.isRunning) {
            return this.overallTime + this.getTimeElapsedSinceLastStart();
        }

        return this.overallTime;
    }
}
